package com.merkost.handpass.admin.auth

import kotlinx.coroutines.await
import com.varabyte.kobweb.core.AppGlobals
import kotlin.js.Promise
import kotlin.js.json

private val supabaseModule: dynamic by lazy {
    js("require('@supabase/supabase-js')")
}

data class AuthSession(
    val accessToken: String,
    val refreshToken: String,
    val userId: String,
    val email: String
)

private val SUPABASE_URL: String = AppGlobals.getValue("admin.supabaseUrl")
private val SUPABASE_ANON_KEY: String = AppGlobals.getValue("admin.supabaseAnonKey")

object SupabaseAuthClient {
    private val client: dynamic by lazy {
        supabaseModule.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, kotlin.js.json(
            "auth" to kotlin.js.json(
                "detectSessionInUrl" to true,
                "flowType" to "implicit"
            )
        ))
    }

    suspend fun signIn(email: String, password: String): Result<AuthSession> {
        return try {
            val result = (client.auth.signInWithPassword(
                json("email" to email, "password" to password)
            ).unsafeCast<Promise<dynamic>>()).await()
            val error = result.error
            if (error != null) {
                Result.failure(Exception(error.message.unsafeCast<String>()))
            } else {
                val session = result.data.session
                Result.success(extractSession(session))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun signInWithGoogle(): Result<Unit> {
        return try {
            val result = (client.auth.signInWithOAuth(
                json(
                    "provider" to "google",
                    "options" to json(
                        "redirectTo" to kotlinx.browser.window.location.origin
                    )
                )
            ).unsafeCast<Promise<dynamic>>()).await()
            val error = result.error
            if (error != null) {
                Result.failure(Exception(error.message.unsafeCast<String>()))
            } else {
                Result.success(Unit)
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun signOut(): Result<Unit> {
        return try {
            (client.auth.signOut().unsafeCast<Promise<dynamic>>()).await()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun getSession(): AuthSession? {
        return try {
            val result = (client.auth.getSession().unsafeCast<Promise<dynamic>>()).await()
            val session = result.data?.session ?: return null
            extractSession(session)
        } catch (_: Exception) {
            null
        }
    }

    fun onAuthStateChange(callback: (event: String, session: AuthSession?) -> Unit): () -> Unit {
        val subscription = client.auth.onAuthStateChange { event: String, sessionDynamic: dynamic ->
            val session = try {
                if (sessionDynamic != null) extractSession(sessionDynamic) else null
            } catch (_: Exception) {
                null
            }
            callback(event, session)
        }
        return { runCatching { subscription.data.subscription.unsubscribe() } }
    }

    private fun extractSession(session: dynamic): AuthSession {
        return AuthSession(
            accessToken = session.access_token.unsafeCast<String>(),
            refreshToken = session.refresh_token.unsafeCast<String>(),
            userId = session.user.id.unsafeCast<String>(),
            email = session.user.email.unsafeCast<String>()
        )
    }
}
